import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminHeader from '../components/admin/AdminHeader';
import { getApiErrorMessage, logError } from '../utils/apiError';
import authService from '../services/authService';
import api from '../services/api';

// Define constants directly since constants.js might be missing
const ENV = process.env.NODE_ENV || 'development';
const POLL_INTERVAL = 60000; // 1 minute
const CURRENT_API_URLS = {
  apiUrl: 'http://localhost:4100',
  cdnUrl: 'http://localhost:4101',
  ssrUrl: 'http://localhost:3001'
};

function AdminPage() {
    const [widgetStatus, setWidgetStatus] = useState('Checking...');
    const [ssrStatus, setSsrStatus] = useState('Checking...');
    const [lastPing, setLastPing] = useState(null);
    const [serverTime, setServerTime] = useState(null);
    const [widgetFileStatus, setWidgetFileStatus] = useState('Not checked');
    const [adminUser, setAdminUser] = useState(null);
    const [statusError, setStatusError] = useState(null);
    
    const navigate = useNavigate();

    useEffect(() => {
        console.log('AdminPage - Component mounted');
        // Check if user is logged in as admin using authService
        if (!authService.isAdminLoggedIn()) {
            console.log('AdminPage - Not authenticated, redirecting to login');
            navigate('/admin/login');
            return;
        }
        
        // Get admin user data from authService
        const userData = authService.getAdminUser();
        
        if (!userData) {
            console.log('AdminPage - No admin user data, redirecting to login');
            navigate('/admin/login');
            return;
        }
        
        console.log('AdminPage - User data:', userData);
        setAdminUser(userData);
    }, [navigate]);

    const checkWidget = async () => {
        try {
            setStatusError(null);
            // Test backend health first
            console.log('AdminPage - Checking health endpoint');
            const healthResponse = await api.getWidgetStatus();
            console.log('AdminPage - Widget status response:', healthResponse.data);
            
            const statusData = healthResponse.data;
            setWidgetStatus(statusData.status);
            setLastPing(statusData.lastPing);
            setServerTime(statusData.serverTime);
            
            // Check SSR app health
            try {
                console.log('AdminPage - Checking SSR health endpoint');
                const ssrResponse = await fetch(`${CURRENT_API_URLS.ssrUrl}/health`);
                if (ssrResponse.ok) {
                    const ssrData = await ssrResponse.json();
                    setSsrStatus(ssrData.status || 'ok');
                } else {
                    setSsrStatus('error');
                }
            } catch (ssrError) {
                logError('AdminPage - SSR health check error', ssrError);
                setSsrStatus('unreachable');
            }
            
            // Optional: Test widget file - only in production
            if (ENV === 'production') {
                try {
                    console.log('AdminPage - Checking widget file:', `${CURRENT_API_URLS.cdnUrl}/widget.js`);
                    const widgetResponse = await fetch(`${CURRENT_API_URLS.cdnUrl}/widget.js`);
                    setWidgetFileStatus(widgetResponse.ok ? 'Accessible' : 'Not accessible');
                } catch (cdnError) {
                    logError('AdminPage - CDN check error', cdnError);
                    setWidgetFileStatus('CDN not reachable');
                }
            } else {
                setWidgetFileStatus('CDN check disabled in development');
            }
        } catch (error) {
            const errorMessage = getApiErrorMessage(error);
            logError('AdminPage - Status check error', error);
            setWidgetStatus(`Error`);
            setStatusError(`Failed to check status: ${errorMessage}`);
            
            // Check for auth errors
            if (error.response && (error.response.status === 401 || error.response.status === 403)) {
                console.error('Authentication error:', error.response.status);
                
                // Clear credentials and redirect to login using authService
                authService.adminLogout();
                navigate('/admin/login');
            }
        }
    };

    useEffect(() => {
        if (adminUser) {
            checkWidget();
            const interval = setInterval(checkWidget, POLL_INTERVAL);
            return () => clearInterval(interval);
        }
    }, [adminUser]);

    if (!adminUser) return <div>Loading admin dashboard...</div>;

    return (
        <div>
            <AdminHeader adminUser={adminUser} />
            <div style={{ 
                padding: '20px',
                margin: '20px 0',
                border: '1px solid #ddd',
                borderRadius: '4px'
            }}>
                <h2>Widget Status</h2>
                <p>Environment: {ENV}</p>
                <p>API URL: {CURRENT_API_URLS.apiUrl}</p>
                <p>Backend Status: <span style={{
                    color: widgetStatus === 'ok' ? 'green' : 'red',
                    fontWeight: 'bold'
                }}>{widgetStatus}</span></p>
                <p>SSR App Status: <span style={{
                    color: ssrStatus === 'ok' ? 'green' : 
                           ssrStatus === 'Checking...' ? 'gray' : 'red',
                    fontWeight: 'bold'
                }}>{ssrStatus}</span></p>
                <p>Widget File: <span style={{
                    color: widgetFileStatus === 'Accessible' ? 'green' : 
                           widgetFileStatus === 'CDN check disabled in development' ? 'gray' : 'red',
                    fontWeight: 'bold'
                }}>{widgetFileStatus}</span></p>
                {lastPing && <p>Last Ping: {new Date(lastPing).toLocaleString()}</p>}
                {serverTime && <p>Server Time: {new Date(serverTime).toLocaleString()}</p>}
                {statusError && (
                    <div style={{ 
                        color: 'red', 
                        backgroundColor: '#ffeeee', 
                        padding: '10px', 
                        borderRadius: '4px', 
                        marginTop: '10px' 
                    }}>
                        {statusError}
                    </div>
                )}
                <button 
                    onClick={checkWidget}
                    style={{
                        padding: '8px 16px',
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                    }}
                >
                    Check Again
                </button>
            </div>
        </div>
    );
}

// Make sure this is properly exported as default
export default AdminPage;